<template>
  <b-container fluid class="configurator d-flex flex-column">
    <b-row v-if="!isHub && !isConfigurator" class="flex-none py-3">
      <b-col class="d-flex flex-column align-items-center justify-content-center">
        <img :src="logoUrl || '/static/images/header.png'" class="logo" :alt="specialCases.title" />
        <p class="d-none d-md-block mt-3 mb-0">Build and personalize your garage door.</p>
      </b-col>
      <b-col cols="12" sm="6" md="4" class="d-flex align-items-center justify-content-center">
        <account-manager />
      </b-col>
    </b-row>
    <b-row class="flex-fill">
      <b-col md="8" class="visualizer d-flex flex-column flex-fill py-3">
        <aside
          v-if="!currentView"
          class="d-flex flex-wrap justify-content-around bg-secondary text-light mb-2"
        >
          <small class="d-flex py-2 px-4">
            <img src="/static/images/keyboard.svg" height="20" role="presentation" class="mr-2" />
            Hold spacebar and drag image to pan
          </small>
          <small class="d-flex py-2 px-4">
            <img src="/static/images/hand.svg" height="20" role="presentation" class="mr-2" />
            On touch devices, use two fingers to pan
          </small>
        </aside>
        <div class="d-flex flex-column flex-fill" style="position: relative; min-height: 200px;">
          <div class="d-flex flex-column flex-fill">
            <render-fader
              :src="currentRender"
              v-show="currentView"
            />
            <masking
              :interactive="isMaskingMode"
              v-show="!currentView"
              ref="masking"
            />
          </div>
          <spinner v-show="rendering" />
        </div>
        <div v-if="!isHub" class="d-flex align-items-center flex-wrap flex-none">
          <div class="d-flex align-items-center flex-auto pt-3 mr-3">
            <span class="mr-3">Progress:</span>
            <b-progress
              :value="progress.value"
              :max="progress.max"
              class="flex-auto"
            />
          </div>
          <view-picker v-if="renderProject && shellProject" class="ml-auto pt-3" />
        </div>
        <div class="pt-3 disclaimer">
          <small>
            <strong>Please Note</strong>: {{specialCases.disclaimer}}
            <a href="https://www.chiohd.com/privacy-policy" target="_blank"> Privacy Policy</a> |
            <a href="https://www.chiohd.com/terms-conditions" target="_blank"> Terms &amp; Conditions</a> |
            <a href="https://www.chiohd.com/california-privacy" target="_blank"> California Privacy Policy</a>
          </small>
        </div>
      </b-col>
      <b-col md="4" class="sidebar d-flex flex-column flex-fill p-0">
        <swiper
          ref="sidebar"
          :options="{ initialSlide: getSlideIndex('configurator') }"
          class="swiper-no-swiping"
        >
          <swiper-slide id="projects">
            <projects
              :rendering="rendering"
              @dimensionChange="fetchLayerPattern"
            />
          </swiper-slide>
          <swiper-slide id="products">
            <spinner v-if="loadingData" :size="96" />
            <template v-else>
              <div class="flex-fill flex-auto" style="position: relative;">
                <options-picker
                  v-if="currentLayer"
                  :products="products"
                  :loading-pattern="loadingPattern"
                  :loading-data="loadingData"
                  @picked="rerender()"
                  @changeLoadingDoor="changeLoadingDoor($event)"
                />
                <spinner v-if="!currentLayer ||
                  (currentLayer.steps && !currentLayer.steps.length)" />
              </div>
              <footer
                v-if="currentProduct"
                class="d-flex justify-content-between flex-none pt-3 px-2"
              >
                <SpinnerButton
                  type="submit"
                  variant="primary"
                  @click.prevent="() => validateForSummary(setCurrentSlide)"
                  :disabled="isValidating"
                  :loading="isValidating"
                  class="flex-auto overflow-ellipsis mx-2"
                >
                  <span class="d-none d-xl-inline-block">View</span> Summary
                  <span class="d-none d-xl-inline-block"> &amp; Request a Quote</span>
                </SpinnerButton>
              </footer>
              <footer
                class="d-flex justify-content-between flex-none pt-2 pb-3 px-2"
              >
                <template v-if="isConfigurator">
                  <b-btn
                    @click.prevent="() => handleEmbeddedClose(true)"
                    class="overflow-ellipsis mx-2 flex-auto"
                  >
                    Cancel
                  </b-btn>
                  <b-btn
                    @click.prevent="() => handleEmbeddedClose()"
                    variant="primary"
                    :disabled="
                      rendering || loadingPattern || !currentProduct ||
                      !currentProduct.renderedImage
                    "
                    class="overflow-ellipsis mx-2 flex-auto"
                  >
                    Apply
                  </b-btn>
                </template>
                <b-btn
                  v-else
                  @click.prevent="setCurrentSlide(['projects/list', 'configurator/projects'])"
                  class="overflow-ellipsis mx-2"
                  :disabled="!currentLayer.physicalWidth && !currentLayer.physicalHeight"
                >
                  <i class="fa fa-home mr-2" />
                  Place on Project
                </b-btn>
                <b-btn
                  v-if="renderProject && renderProject.tag === 'uploaded'"
                  @click.prevent="setCurrentSlide(['projects/item', 'configurator/projects'])"
                  class="overflow-ellipsis mx-2"
                >
                  Edit Openings
                </b-btn>
              </footer>
            </template>
            <div v-if="loadingDoor" class="overlay"/>
            <spinner v-if="loadingDoor" :size="64" />
          </swiper-slide>
          <swiper-slide id="summary">
            <b-container v-if="currentLayer" fluid class="steps flex-fill">
              <div v-for="layer in summaryLayers" :key="layer.id">
                <b-col v-if="applyToLayers.length > 1" style="text-align: center;">
                  <div class="btn btn-link my-3" style="padding: 0; pointer-events: none;">
                    {{layer.name}}
                  </div>
                </b-col>
                <step v-for="step of getSummarySteps(layer)" :key="step.name" :step="step" />
              </div>
            </b-container>

            <footer class="px-2 py-3">
              <share>
                <b-btn @click.prevent="setCurrentSlide('configurator/products')">
                  Back
                </b-btn>
              </share>
            </footer>
          </swiper-slide>
        </swiper>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import axios from 'axios';
import get from 'lodash.get';
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import Spinner from '@/components/utility/Spinner';
import SpinnerButton from '@/components/utility/SpinnerButton';
import AccountManager from '@/components/AccountManager';
import Step from '@/components/Step';
import OptionsPicker from '@/components/OptionsPicker';
import Projects from '@/components/Projects';
import RenderFader from '@/components/masking/RenderFader';
import Masking from '@/components/masking/Module';
import ViewPicker from '@/components/ViewPicker';
import Share from '@/components/Share';
import { setProductPixelDimensions } from '@/helpers/dimensions';
import saveRenderProject from '@/helpers/project';
import { isSizeStep } from '@/helpers/step';
import { sendPostMessage } from '@/helpers/embedded';

function pick(source, keys, initial = {}) {
  return keys.reduce((acc, key) => {
    acc[key] = source[key];
    return acc;
  }, initial);
}

export default {
  name: 'configurator',
  props: {
    products: Array,
  },
  data() {
    return {
      slides: [
        'projects',
        'products',
        'summary',
      ],
      patternParamsGetters: {
        // if any of these return null, they will be considered optional checks
        // (and therefore pass validation)
        model: (layer, chiData) => chiData.getProductModelName(),
        derivedModel: (layer, chiData) => chiData.getDerivedModelName(),
        designNumber: (layer, chiData) => chiData.getDesignNumber(),
        productLine: (layer, chiData) => chiData.getProductLineName(),
        // convert feet to inches
        width: layer => get(layer, 'physicalWidth', 0) * 12,
        height: layer => get(layer, 'physicalHeight', 0) * 12,
      },
      cachedPatternParams: {},
      pattern: undefined,
      loadingPattern: false,
      loadingData: true,
      loadingDoor: false,
      renderingPromise: Promise.resolve(),
      rendering: false,
      logoUrl: undefined,
      isValidating: false,
    };
  },
  computed: {
    ...mapState([
      'renderProject',
      'shellProject',
      'currentLayer',
      'currentView',
      'currentRender',
      'currentSlides',
      'specialCases',
      'currentSavedDoor',
    ]),
    ...mapGetters([
      'isHub',
      'isConfigurator',
      'applyToLayers',
      'currentProject',
      'currentProduct',
      'getSlideIndex',
      'currentDealer',
    ]),
    progress() {
      const steps = this.currentLayer ? this.currentLayer.steps : [];
      return {
        value: steps.length > 3 ? this.currentLayer.currentStepIndex + 1 : 0,
        max: steps.length || 0,
      };
    },
    isMaskingMode() {
      return this.currentSlides.configurator === 'projects'
        && this.currentSlides.projects === 'item';
    },
    summaryLayers() {
      if (this.currentView) {
        return this.applyToLayers.filter(l => l.id === this.currentView.id);
      }
      return this.applyToLayers;
    },
  },
  watch: {
    async isMaskingMode(isMaskingMode) {
      // save project upon leaving masking mode
      if (!isMaskingMode) {
        if (this.renderProject) {
          await saveRenderProject(this.renderProject);
        }
      }

      // re-render to either re-apply 'hidden' products, or hide them for mask editing
      this.rerender();
    },
    renderProject(renderProject) {
      // if there's no project to show, switch to layer view
      if (!renderProject && !this.currentView) {
        this.setCurrentView(this.currentLayer);
        this.setCurrentSlide('projects/list');
      }
    },
    currentProject(currentProject, previousProject) {
      // sync RW
      window.Renoworks.ProjectController.setCurrentProject(currentProject);

      // re-render to apply products onto new project
      if (currentProject && previousProject && currentProject.folder !== previousProject.folder) {
        this.rerender();
      }
    },
    currentView(currentView) {
      // force away from project screen if door view selected
      if (currentView && this.currentSlides.configurator === 'projects' && !this.isHub) {
        this.setCurrentSlide('configurator/products');
      }

      // re-render to bring previously hidden elements into sync with latest render
      this.rerender();
    },
    currentSlides: {
      handler() {
        // map to slideIndex
        const slideIndex = this.getSlideIndex('configurator');

        // swipe to slide
        if (slideIndex >= 0) {
          this.$refs.sidebar.swiper.slideTo(slideIndex);
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapMutations([
      'setShellProject',
      'setCurrentLayer',
      'setCurrentView',
      'setCurrentRender',
      'setNoDealerQuotes',
      'setDealerID',
      'setDealerEmail',
    ]),
    ...mapActions([
      'setCurrentSlide',
    ]),
    async validateForSummary(continueFunction) {
      // TODO - Same code is copied from src/components/share/Email.vue - sendLead()
      let valid = true;
      try {
        this.isValidating = true;
        const chiData = window.Renoworks.CHIData;
        const url = `${window.Renoworks.Util.getBaseApiUrl()}/CHIValidate`;

        const layer = this.currentLayer;
        const width = layer.physicalWidth;
        const height = layer.physicalHeight;
        const { code } = this.$route.query;

        // Get selected Color
        const productSteps = this.currentProduct.getSteps();
        const colorStep = productSteps.find(step => step.name === 'Color');
        let colorCategory = colorStep.sections[0].categories.find(
          item => item.selected);
        if (!colorCategory) {
          colorCategory = colorStep.sections[0].categories[0];
        }
        // Required: Solid Color, Accents Woodtones, Morton Powder Coat, RAL Powder Coat, etc.
        let selectedColorCategoryName = null;
        // Required
        let selectedColorName = null;
        // Required if user choose RAL category else null
        let selectedRalColorCategory = null;
        // Required in all the cases but value will be 0 for RAL, Morton, CHI powder coats
        let colorID = 0;
        // Required in all the cases
        const orderModeEnum = 10;
        const orderSourceEnum = 2;
        // Conditional: The omerware code provided by CHI, the OwCode from the Color object
        let outsideColorOmerWareCode = null;
        // Conditional: Available only when AccentWoodTones else 0
        let frontAccentImageId = 0;
        // Conditional: Available only when RAL color
        let frontRALColorNumber = null;
        // Conditional: Available only when Morton color else 0
        let frontMortonColorId = 0;
        // Conditional: Available only when Powder Coat color
        let isOutsideColorStandardPowderCoat = null;
        // Conditional: Available only when Morton color
        let isOutsideColorCustomPowderCoat = null;
        // Conditional: Available only when Morton color
        let outsideColorCodeName = null;

        if (colorCategory) {
          selectedColorCategoryName = colorCategory.name;
          let selectedColorObj = colorCategory.items.find(
            item => item.selected);
          if (!selectedColorObj) {
            selectedColorObj = colorCategory.items[0];
          }
          selectedColorName = selectedColorObj.name;
          if (selectedColorObj.subOptions && selectedColorObj.subOptions.length) {
            selectedRalColorCategory = selectedColorObj.subOptions[0].name;
          }

          // Get color ID
          const colorOptions = chiData.getProductModel().colorOptions;

          let selectedColor = null;
          selectedColor = colorOptions.standardFrontColorOptions.find(
            item => item.displayName.toLowerCase() === selectedColorName.toLowerCase());
          if (selectedColor) {
            colorID = selectedColor.id;
          }
          if (!selectedColor) {
            selectedColor = colorOptions.chiPowderCoatOptions.find(
              (item) => {
                if (item.displayName) {
                  return item.displayName.toLowerCase() === selectedColorName.toLowerCase();
                }
                return item.name.toLowerCase() === selectedColorName.toLowerCase();
              });
            if (selectedColor) {
              colorID = 0;
              isOutsideColorStandardPowderCoat = true;
              outsideColorOmerWareCode = selectedColor.owCode;
            }
          }
          if (!selectedColor) {
            selectedColor = colorOptions.woodgrainPowderCoatOptions.find(
              item => item.displayName.toLowerCase() === selectedColorName.toLowerCase());
            if (selectedColor) {
              colorID = selectedColor.id;
              isOutsideColorStandardPowderCoat = false;
              outsideColorOmerWareCode = selectedColor.owCode;
            }
          }
          if (!selectedColor) {
            selectedColor = colorOptions.accentFrontColorOptions.find(
              item => item.displayName.toLowerCase() === selectedColorName.toLowerCase());
            if (selectedColor) {
              frontAccentImageId = selectedColor.imageNumber;
              colorID = selectedColor.id;
            }
          }
          if (!selectedColor) {
            selectedColor = colorOptions.ralColorOptions.find(
              item => item.displayName === selectedColorName);
            if (selectedColor) {
              frontRALColorNumber = selectedColor.displayName;
              isOutsideColorStandardPowderCoat = false;
              isOutsideColorCustomPowderCoat = true;
              outsideColorOmerWareCode = 15;
            }
          }
          if (!selectedColor) {
            selectedColor = colorOptions.mortonColorOptions.find(
              item => item.displayName === selectedColorName);
            if (selectedColor) {
              frontMortonColorId = selectedColor.id;
              isOutsideColorStandardPowderCoat = false;
              isOutsideColorCustomPowderCoat = true;
              outsideColorOmerWareCode = 1000;
              outsideColorCodeName = 'MOR';
            }
          }
          if (!selectedColor) {
            selectedColor = colorOptions.customFrontColorOptions.find(
              item => item.displayName === selectedColorName);
            if (selectedColor) {
              colorID = selectedColor.id;
            }
          }
        }

        let request = {
          ...chiData.getSummary(width * 12),
          dealerNumber: code,
          selectedOutsideColorCategory: selectedColorCategoryName,
          selectedOutsideColorName: selectedColorName,
          selectedOutsideRalColorCategory: selectedRalColorCategory,
          outsideColorId: colorID,
          frontMortonColorId,
          frontRALColorNumber,
          frontAccentImageId,
          isOutsideColorStandardPowderCoat,
          isOutsideColorCustomPowderCoat,
          outsideColorOmerWareCode,
          outsideColorCodeName,
          orderModeEnum,
          orderSourceEnum,
          quantity: this.quantity,
          doorHeightWidth: {
            heightFeet: height ? Math.floor(height) : undefined,
            heightInch: height ? Math.round((height - Math.floor(height)) * 12) : undefined,
            widthFeet: width ? Math.floor(width) : undefined,
            widthInch: width ? Math.round((width - Math.floor(width)) * 12) : undefined,
          },
        };
        const removeEmpty = (obj) => {
          Object.keys(obj).forEach(k => (obj[k] === null && obj[k] !== undefined) && delete obj[k]);
          return obj;
        };

        request = removeEmpty(request);

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(request),
        });
        const content = await response.text();

        let errorMessage = null;
        const messageTitle = 'Error!';
        if (content && content.substring(0, 3) === '200') {
          const responseJSON = JSON.parse(content.substring(4));
          if (responseJSON && responseJSON.statusCode !== '200') {
            errorMessage = 'Failed to validate. An error occurred.';
            if (responseJSON.errors && responseJSON.errors.length) {
              errorMessage = responseJSON.errors.join(' ');
            }
          }
        } else {
          errorMessage = 'Failed to validate. An error occurred.';
        }
        if (errorMessage) {
          valid = false;
          this.$root.alert(
            errorMessage,
            messageTitle,
          );
        }
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      } finally {
        this.isValidating = false;
      }
      if (valid) {
        continueFunction('configurator/summary');
      }
    },
    getSizeStep() {
      return Share.sizeStep;
    },
    getSummarySteps(layer) {
      // always use the layer's size step (since it can differ between identical products);
      // only use the layer's other steps if/when it has a product, meaning there are more
      // than one layer present, each with unique products
      const summarySteps = (layer.product ? layer : this.currentLayer).steps
        .map((step) => {
          if (isSizeStep(step)) {
            return layer.steps.find(s => isSizeStep(s, this.specialCases));
          }
          return step;
        })
        .filter(step => step && step.selected);

      return summarySteps;
    },
    // ensure all required fields are set
    isLayerRenderable(layer = this.currentLayer, chiData = window.Renoworks.CHIData) {
      return Object.values(this.patternParamsGetters).every((check) => {
        const value = check(layer, chiData);
        return value || value === null; // null -> optional check
      });
    },
    getPatternParams(layer = this.currentLayer, chiData = window.Renoworks.CHIData) {
      return Object.entries(this.patternParamsGetters).reduce(
        (acc, [name, check]) => ({
          ...acc,
          [name]: check(layer, chiData),
        }),
        {},
      );
    },
    getCachePatternKey(patternParams) {
      return Object.entries(patternParams).reduce(
        (acc, [key, value]) => acc.concat(`${key}${value}`),
        '',
      );
    },
    cachePattern(patternParams, pattern) {
      const key = this.getCachePatternKey(patternParams);
      this.cachedPatternParams[key] = pattern;
    },
    getCachedPattern(patternParams) {
      const key = this.getCachePatternKey(patternParams);
      return this.cachedPatternParams[key];
    },
    async fetchPattern(patternParams) {
      const cachedPatternData = this.getCachedPattern(patternParams);
      if (cachedPatternData) {
        return cachedPatternData;
      }

      // ping CHI API for custom step behaviour modifications and pattern info for render
      const url = `${window.Renoworks.Util.getBaseApiUrl()}/CHIPattern`;
      const { status, data } = await axios.get(url, {
        params: patternParams,
      });

      // abort, if necessary
      if (data.error) {
        throw data.error;
      } else if (status !== 200) {
        throw new Error(`Product pattern returned HTTP status ${status}`);
      }

      this.cachePattern(patternParams, data);
      return data;
    },
    async fetchLayerPattern(layer = this.currentLayer, product = this.currentProduct) {
      const chiData = window.Renoworks.CHIData;
      if (!product || !chiData.getProductModel()) {
        return '';
      }

      try {
        const patternParams = this.getPatternParams(layer, chiData);
        const { pattern, model } = await this.fetchPattern(patternParams);
        const layerSizeKey = layer.getPatternKey();

        // Blank pattern results in rwd default pattern being used by the RE
        this.pattern = pattern || '';
        // store in the product for later use rendering layers with different sizes
        product.layerPatterns[layerSizeKey] = this.pattern;

        // Special case for Flush Carriage House and Overlay Carriage House
        const widthTab = product.tabs.find(tab => tab.name === 'Width');
        if (widthTab) {
          product.selectGridBySelection(widthTab, { name: 'N/A' });
          // Check windowQty, and set width accordingly
          let name;
          switch (model.windowQuantity) {
            default:
            case 2: {
              name = 'Single';
              break;
            }
            case 4: {
              name = 'Double';
              break;
            }
          }
          product.selectGridBySelection(widthTab, { name });
        }

        // if layer steps get reset, use whatever was the last pattern (e.g. skip getting a new one)
        /*
        if (layer.steps && layer.steps.length) {
          // inject extra data while forcing OptionsPicker to re-render
          const { stepNames, tabOptionsFilters } = this.specialCases;
          const thermalStep = layer.steps.find(s => s.name === stepNames.thermalConstruction);
          // only inject it before it is selected, since: redundant
          // but also because selected steps are deselected by the injection
          if (!thermalStep.selected) {
            // extract the relevant thermal requirements array from meta
            const thermalRequirements = meta
              .filter(tr => tr.quality)
              .map(({ modelProperty:
                { numberOfThermometers, cutOutImageUrl, ...modelProperty } = {}, ...tr }) => ({
                ...tr,
                // move numThermos out of descriptions so it doesn't get shown to user
                numberOfThermometers: Number(numberOfThermometers) || 0,
                modelProperty,
                thumbnailImage: cutOutImageUrl,
                rValue: tr.rValue || '', // ensure this always exists as (at least) an empty object
              }));
            product.injectExtraDataToTab(stepNames.thermalConstruction, thermalRequirements);
          }
          product.injectTabOptionsFilters(tabOptionsFilters(model));
        } */

        product.modelNumber = model.modelNumber;

        return this.pattern;
      } catch (err) {
        console.warn(err); // eslint-disable-line no-console

        // NOTE: do not overwrite the existing layer pattern if it fails
        return null;
      }
    },
    async updateProductPatterns() {
      const update = async (layer, product = layer.product) => {
        if (!product) return;

        // calculate and store product dimensions (in pixels) for rendering
        // project ppf is based upon layer dimensions and masked areas
        setProductPixelDimensions(product, layer);

        // get up-to-date pattern string, regardless of render
        product.pattern = await this.fetchLayerPattern(layer, product);
      };

      // update product info for rendering
      this.loadingPattern = true;
      if (this.currentProject && this.currentProject.layers) {
        // door has been placed on a project
        await Promise.all(this.currentProject.layers.map(layer => update(layer)));
      } else {
        // door has NOT (yet) been placed on a project
        await update(this.currentLayer, this.currentProduct);
      }
      this.loadingPattern = false;
    },
    async rerenderProject() {
      const project = window.Renoworks.ProjectController.getCurrentProject();
      if (!this.currentView && this.renderProject) {
        // render project to <masking-module>

        // only attempt to re-render if not currently masking
        const hasProductApplied = project.layers.some(l => l.product);
        if (!this.isMaskingMode && hasProductApplied) {
          // wrap in try...catch since CF throws if no products are currently applied
          // (which can happen easily in CHI)
          try {
            // render via CF controller
            const renderSrc = await window.Renoworks.ProjectController.render();

            // force MM to redraw newly rendered project image
            this.renderProject.renderSrc = renderSrc;
          } catch (err) {
            console.warn(err); // eslint-disable-line no-console
          }
        } else {
          // force MM to redraw using master image
          // (since we don't want to show applied products while masking)
          this.renderProject.renderSrc = this.renderProject.getMasterImage();
        }
      }
    },
    rerender() {
      // make sure previous render(s) finish first
      this.renderingPromise = this.renderingPromise.then(async () => {
        await this.updateProductPatterns();

        // abort if we can't render yet
        if (!this.isLayerRenderable()) return;

        // NOTE: if you do not enable and disable the spinner new renders will not be shown!
        this.rendering = true; // enable the spinner

        if (!window.Renoworks.CHIData.getProductModelName()) {
          await window.Renoworks.CHIData.render(this.currentLayer);
          this.setCurrentRender(window.Renoworks.CHIData.renderedImage);
        } else {
          // TODO - use default ^ for render project as well
          await this.rerenderProject();

          // render product via CF model (e.g. for thumbnails)
          // the saved door image gets updated on step change
          if (this.currentProduct) {
            await this.currentProduct.render();
            this.setCurrentRender(this.currentProduct.renderedImage);
          }
        }

        this.rendering = false; // disable the spinner
      })
        .catch(err => console.warn(err)); // eslint-disable-line no-console
    },
    changeLoadingDoor(value) {
      this.loadingDoor = value;
    },
    loadLogo() {
      const { code } = this.$route.query;
      if (code && /^[a-z0-9]+$/i.test(code)) {
        const src = `https://chiohddoorvision.blob.core.windows.net/dealerlogos/${code}.png`;
        const img = document.createElement('img');
        img.onload = () => {
          this.logoUrl = src;
        };
        img.onerror = () => {
          this.logoUrl = null;
        };
        img.src = src;
      } else {
        this.logoUrl = null;
      }
    },
    handleEmbeddedClose(cancel = false) {
      let currentProduct;
      if (this.currentProduct) {
        // serialize currentProduct model to restoring later
        currentProduct = pick(
          this.currentProduct,
          [
            'renderedImage',
            'name',
            'rwd',
          ],
          {
            gridSettings: this.currentProduct.getGridSettings(),
          },
        );
      }
      let currentLayer;
      if (this.currentLayer) {
        currentLayer = pick(
          this.currentLayer,
          [
            'physicalWidth',
            'physicalHeight',
            'currentStepIndex',
            'farthestStepIndex',
          ],
        );
      }
      sendPostMessage({
        action: cancel ? 'close' : 'apply',
        payload: {
          currentProduct,
          currentLayer,
        },
      });
    },
  },
  async created() {
    window.Renoworks.Masking.perspectiveType = 'garagedoor';

    // create a 'shell' project to hold doors independent of a loaded project
    const shellProject = new window.Renoworks.Project();

    // if request-quote query params are passed in, then persist them in global store
    this.setNoDealerQuotes(this.$route.query.q !== undefined);
    this.setDealerID(this.$route.query.d);
    this.setDealerEmail(this.$route.query.email);
    this.loadLogo();

    // create an initial 'view' layer
    const layer = new window.Renoworks.Layer('Door');
    layer.id = 1;
    layer.name = 'Door 1';

    // uploaded projects should show no shadows, neither should grid renders
    layer.shadows = 2;

    // and door type
    layer.doorShape = 'rectangular';

    // add default dimensions (single car, in feet)
    layer.physicalWidth = 0;
    layer.physicalHeight = 0;

    // allow sending overrides via postMessage when isConfigurator
    if (window.layerInitDefaults) {
      Object.assign(layer, window.layerInitDefaults);
    }

    // find perspective from project dimensions
    layer.perspective = new window.Renoworks.Perspective();
    layer.perspective.initWithProjectDimensions(
      250,
      250,
      layer.physicalHeight ? layer.physicalWidth / layer.physicalHeight : undefined,
    );

    // add layer to project
    shellProject.layers.push(layer);

    // start by looking behind Door #1
    this.setShellProject(shellProject);
    this.setCurrentLayer(layer);
    this.setCurrentView(layer);

    // Load data
    const CHIData = window.Renoworks.CHIData;
    CHIData.load().then(() => {
      this.loadingData = false;
    }).catch(() => {
    });

    if (this.isHub) {
      this.setCurrentSlide('configurator/projects');
    }
  },
  components: {
    Spinner,
    SpinnerButton,
    AccountManager,
    Step,
    OptionsPicker,
    Projects,
    RenderFader,
    Masking,
    ViewPicker,
    Share,
  },
};
</script>

<style lang="scss">
@import "~@/assets/styles/variables.chi.scss";

.configurator {
  height: 100%;

  .swiper-container {
    height: 100%;
    margin: 0;

    .swiper-wrapper {
      .swiper-slide {
        display: flex;
        flex-direction: column;
        width: 100% !important;

        .overlay {
          position: absolute;
          width: 100%;
          height: 100%;
          background: #333;
          opacity: 0.5;
          top: 0px;
          left: 0px;
        }
      }
    }
  }

  .visualizer {
    background-color: $gray-300;

    .progress {
      min-width: 200px;
    }

    .disclaimer {
      a {
        color: inherit;
        text-decoration: none;

        &:hover,
        &:focus {
          color: inherit;
          text-decoration: underline;
        }
      }
    }
  }

  .step {
    margin-bottom: $spacer;

    .tabs {
      .nav-tabs {
        padding: ($spacer / 2) ($spacer / 2) 0;
      }
    }

    .step-cards { // @TODO: why is this required?
      display: flex;
      flex-wrap: wrap;
    }

    .card {
      display: inline-flex;
      width: calc(50% - #{$spacer}); // most cards have two divs, so 50%
      margin: ($spacer / 2);

      > .card-header {
        flex: auto;
      }
    }

    &.collapsable {
      &:not(.disabled):not(.active) {
        > .card-header {
          cursor: pointer;
        }
      }
    }

    &.active {
      > .card-header {
        color: $primary;
      }
    }

    &.disabled {
      opacity: 0.5;
    }
  }

  #summary {
    .step {
      // Non-active tabs are hidden in the summary,
      // so no borders are needed
      .nav-link {
        border: none;
      }

      .nav-link,
      .card {
        cursor: default;

        // Hide non-active elements
        &:not(.active) {
          display: none;
        }
      }
    }
  }
}

@import "~@/assets/styles/configurator.chi.scss";
</style>



// WEBPACK FOOTER //
// src/components/Configurator.vue